import { Offline } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ConnectedDmsRow, type DmsAccountDisplay } from '@/matter/components/matter-settings/ConnectedDmsRow'
import { MatterFolderSyncRow } from '@/matter/components/matter-settings/MatterFolderSyncRow'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { useOpenConnectedAccountsSettings } from '@/settings-modal/hooks/useOpenConnectedAccountsSettings'

interface MatterSettingsConnectedAccountCardProps {
  canChooseFolder: boolean
  canDisconnectFolder: boolean
  isConnector: boolean
  connectorLabel: string | null
  driveSyncEnabled: boolean
  error: string | null
  loading: boolean
  onChooseFolder: () => void
  onDisconnectFolder: () => void
  disconnectingFolder?: boolean
  syncedFolderName: string | null
  lastSyncedAt: string | null
  syncFailureMessage: string | null
  accountDisplay: DmsAccountDisplay | null
}

export function MatterSettingsConnectedAccountCard({
  canChooseFolder,
  canDisconnectFolder,
  isConnector,
  connectorLabel,
  driveSyncEnabled,
  error,
  loading,
  onChooseFolder,
  onDisconnectFolder,
  disconnectingFolder = false,
  syncedFolderName,
  lastSyncedAt,
  syncFailureMessage,
  accountDisplay,
}: MatterSettingsConnectedAccountCardProps) {
  const openConnectedAccountsSettings = useOpenConnectedAccountsSettings()

  return (
    <div
      data-testid="matter-settings-connected-account-card"
      className="w-full max-w-[946px] overflow-hidden rounded-lg border border-brand-neutral-200 bg-white"
    >
      <div className="flex min-h-11 items-center gap-1 border-b border-brand-neutral-200 px-4 py-2">
        <p className="min-w-0 flex-1 font-rethink-sans text-sm font-semibold leading-5 text-brand-neutral-500">Connected Account</p>
      </div>

      {loading && (
        <div className="flex justify-center px-4 py-8">
          <CircularProgressContinuousSized size={24} thickness={7} />
        </div>
      )}

      {!loading && error && <p className="px-4 py-6 text-sm text-red-600">{error}</p>}

      {!loading && !error && !driveSyncEnabled && <p className="px-4 py-6 text-sm text-brand-neutral-600">Document management integrations are not enabled.</p>}

      {!loading && !error && driveSyncEnabled && accountDisplay == null && (
        <div className="grid grid-cols-[60px_minmax(0,1fr)] items-center gap-4 px-4 py-4 sm:flex">
          <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-md border border-brand-neutral-200 bg-white">
            <Offline className="h-6 w-6 text-true-gray-300" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-rethink-sans text-base font-semibold leading-7 text-brand-neutral-950">No Connected Account</p>
            <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">Connect a document management system to sync a folder to this matter.</p>
          </div>
          <Button
            type="button"
            variant="tonal"
            color="primary"
            size="sm"
            className="col-start-2 justify-self-start sm:shrink-0"
            onClick={() => openConnectedAccountsSettings(SettingsEntrySource.MatterSettingsConnectedAccount)}
          >
            Settings
          </Button>
        </div>
      )}

      {!loading && !error && driveSyncEnabled && accountDisplay != null && (
        <>
          <ConnectedDmsRow account={accountDisplay} />
          <MatterFolderSyncRow
            provider={accountDisplay.provider}
            canChooseFolder={canChooseFolder}
            canDisconnectFolder={canDisconnectFolder}
            isConnector={isConnector}
            connectorLabel={connectorLabel}
            disconnecting={disconnectingFolder}
            syncedFolderName={syncedFolderName}
            lastSyncedAt={lastSyncedAt}
            syncFailureMessage={syncFailureMessage}
            onChooseFolder={onChooseFolder}
            onDisconnectFolder={onDisconnectFolder}
          />
        </>
      )}
    </div>
  )
}
